import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import { AccentColor } from "@/lib/colors";

type ButtonVariant = "filled" | "outlined" | "text";

type ButtonOwnProps = {
  color?: AccentColor;
  variant?: ButtonVariant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
};

export type ButtonProps = ButtonOwnProps &
  (
    | (Omit<ComponentPropsWithoutRef<"button">, keyof ButtonOwnProps> & {
        href?: undefined;
      })
    | (Omit<ComponentPropsWithoutRef<"a">, keyof ButtonOwnProps> & {
        href: string;
      })
  );

const sharedBase =
  "inline-flex items-center justify-center text-center gap-2 text-[0.9375rem] font-body font-semibold uppercase tracking-wider leading-[1.75] px-5.5 py-2 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-40 disabled:cursor-not-allowed";

const base = `${sharedBase}  rounded-md active:scale-95 transition-transform duration-150`;

const filledStyles: Record<AccentColor, string> = {
  [AccentColor.Bubblegum]:
    "bg-bubblegum hover:bg-bubblegum/90 text-white focus-visible:outline-bubblegum",
  [AccentColor.Lavender]:
    "bg-lavender hover:bg-lavender/90 text-white focus-visible:outline-lavender",
  [AccentColor.Aegean]:
    "bg-aegean hover:bg-aegean/90 text-white focus-visible:outline-aegean",
  [AccentColor.Success]:
    "bg-success hover:bg-success/90 text-white focus-visible:outline-success",
  [AccentColor.Warning]:
    "bg-warning hover:bg-warning/90 text-white focus-visible:outline-warning",
  [AccentColor.Error]:
    "bg-error hover:bg-error/90 text-white focus-visible:outline-error"
};

const outlineStyle =
  "border border-text-primary/20 text-text-body hover:text-text-primary hover:border-text-primary/60 focus-visible:outline-bubblegum";

const textStyle =
  "text-text-body hover:text-text-primary focus-visible:outline-bubblegum";

export function buttonClass(options?: {
  color?: AccentColor;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  className?: string;
}) {
  const {
    color = AccentColor.Bubblegum,
    variant = "filled",
    fullWidth,
    className
  } = options ?? {};
  if (variant === "text") {
    return `${base} ${textStyle}${fullWidth ? " w-full" : ""}${className ? ` ${className}` : ""}`;
  }
  const styles = variant === "filled" ? filledStyles[color] : outlineStyle;
  return `${base} ${styles}${fullWidth ? " w-full" : ""}${className ? ` ${className}` : ""}`;
}

export function Button({
  color = AccentColor.Bubblegum,
  variant = "filled",
  startIcon,
  endIcon,
  fullWidth,
  className,
  children,
  ...rest
}: ButtonProps) {
  const cls = buttonClass({ color, variant, fullWidth, className });

  if ("href" in rest && rest.href != null) {
    return (
      <a className={cls} {...(rest as ComponentPropsWithoutRef<"a">)}>
        {startIcon}
        {children}
        {endIcon}
      </a>
    );
  }

  return (
    <button className={cls} {...(rest as ComponentPropsWithoutRef<"button">)}>
      {startIcon}
      {children}
      {endIcon}
    </button>
  );
}
